import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Box, Flex, PrimaryPaper } from '@atoms/layout';
import { EditIcon, FaIcon } from '@atoms/icons';
import { TextField } from '@atoms/inputs';
import { Stack } from '@mui/material';

type SectionProps = {
  notes: Note[];
  onSave?: (note: Note) => Promise<void>;
  localizationSubset: string;
};
export function ShiftNotes({ notes, onSave, localizationSubset }: SectionProps) {
  return (
    <>
      <Box>
        {notes.map((item) => (
          <NoteBlock key={item.id} note={item} onSave={onSave} localizationSubset={localizationSubset} />
        ))}
      </Box>
    </>
  );
}

type NoteBlockProps = {
  note: Note;
  onSave?: (note: Note) => Promise<void>;
  localizationSubset: string;
};
function NoteBlock({ note, onSave, localizationSubset }: NoteBlockProps) {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(note.value);
  const [loading, setLoading] = useState(false);
  const onSaveClick = async () => {
    setLoading(true);
    if (note.value !== localValue) {
      const updatedNote = { ...note, value: localValue };
      await onSave?.(updatedNote);
      setLocalValue(updatedNote.value);
    }
    setLoading(false);
    setIsEditing(false);
  };

  return (
    <>
      <PrimaryPaper key={note.id}>
        <Stack direction="column" spacing={2}>
          <Stack alignItems={'center'} direction="row">
            <Typography variant="body1">{note.title}</Typography>
            <Box flex={1} />
            {loading ? (
              <CircularProgress size={28} />
            ) : isEditing ? (
              <IconButton size="small" onClick={() => onSaveClick()}>
                <FaIcon name="check" />
              </IconButton>
            ) : (
              <IconButton size="small" onClick={() => setIsEditing(true)}>
                <EditIcon />
              </IconButton>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <FaIcon name="circle-exclamation" color="warning.main" />
            <Typography variant="body2" color="warning.main">
              <FormattedMessage id={`${localizationSubset}.overrideWarning`} />
            </Typography>
          </Stack>
          <TextField
            multiline
            fullWidth
            minRows={3}
            value={localValue}
            InputProps={{
              readOnly: !isEditing,
            }}
            autoFocus={isEditing}
            placeholder={note.value || isEditing ? '' : intl.formatMessage({ id: 'shifts.noNotes.title' })}
            onChange={(e) => setLocalValue(e.target.value)}
          />
        </Stack>
      </PrimaryPaper>
    </>
  );
}

type Note = { id: string; value: string; title: string };
